import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CARRIER_PROFILE_QUERY_KEY, editCarrierProfile } from './CarrierProfile.service'
import type { CarrierProfileFormValues, CarrierProfile } from './CarrierProfile.types'

interface EditCompanyDetailsDialogPayload {
  readonly carrierProfile: CarrierProfile,
}

export interface EditCompanyDetailsDialogProps {
  readonly payload?: EditCompanyDetailsDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const EditCompanyDetailsDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: EditCompanyDetailsDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<CarrierProfileFormValues>()
  const queryClient = useQueryClient()
  const updateCarrierProfileMutation = useMutation({
    mutationFn: async (data: CarrierProfileFormValues) => {
      await editCarrierProfile(data)
    },
    onError: (error) => setError('root', processServerErrorMessages(error)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIER_PROFILE_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          name: payload?.carrierProfile.name,
          address: payload?.carrierProfile.address,
          city: payload?.carrierProfile.city,
          country: payload?.carrierProfile.country,
          registrationTaxNumber: payload?.carrierProfile.registrationTaxNumber,
          website: payload?.carrierProfile.website,
          additionalNotes: payload?.carrierProfile.additionalNotes,
          types: payload?.carrierProfile.types ?? [],
          transportModes: payload?.carrierProfile.transportModes ?? [],
          qualifications: payload?.carrierProfile.qualifications ?? [],
        })
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<CarrierProfileFormValues> = async (data: CarrierProfileFormValues): Promise<void> => {
    await updateCarrierProfileMutation.mutateAsync({
      ...payload?.carrierProfile,
      ...data,
    })
    onAction()
    analyticsEvent('polestar_carrier_profile_company_updated')
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.cs.carrier_profile.company.edit_company_details')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <KNFormText
            name="name"
            label={t('screens.cs.carrier_profile.company.name')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        <Grid size={12}>
          <KNFormText
            name="address"
            label={t('screens.cs.carrier_profile.company.address')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        <Grid size={6}>
          <KNFormText
            name="city"
            label={t('screens.cs.carrier_profile.company.city')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        <Grid size={6}>
          <KNFormText
            name="country"
            label={t('screens.cs.carrier_profile.company.country')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        <Grid size={12}>
          <KNFormText
            name="registrationTaxNumber"
            label={t('screens.cs.carrier_profile.company.registration_tax_number')}
            control={control}
          />
        </Grid>
        <Grid size={12}>
          <KNFormText
            name="website"
            label={t('screens.cs.carrier_profile.company.website')}
            control={control}
          />
        </Grid>
        <Grid size={12}>
          <KNFormText
            name="additionalNotes"
            label={t('screens.cs.carrier_profile.company.additional_notes')}
            control={control}
            rows={4}
          />
        </Grid>
      </Grid>
    </KNDialog>
  )
}

export default EditCompanyDetailsDialog
